import { getLocalJitsiAudioTrack } from '../../base/tracks/functions.any';
import { hideNotification } from '../../notifications/actions';
// @ts-ignore
import { setCalibrationDebugInfo } from './NkfAecEffect.js';
import logger from './logger';
const CAL_WARN_UID = 'nkf-aec-calibration-warning';
const PEAK_FLOOR_LOW = 5; // below this GCC-PHAT SNR the result is unreliable
const MAX_ATTEMPTS = 2; // audible chime plays at most this many times per mic-on
/**
 * One-shot in-situ calibration at join. On the first mic-on (and again after a
 * device change), play a short chime through the SAME far-end path the AEC uses
 * and measure the echo round-trip delay with GCC-PHAT, then set it immediately
 * so echo cancellation works from the very start — instead of waiting ~2 s for
 * the passive tracker to converge from live remote speech.
 *
 * The result is NOT persisted across sessions (speaker volume / headset / room
 * change a stale delay into a feedback loop). If the measurement is low
 * confidence we stop after MAX_ATTEMPTS and defer to the passive DelayTracker,
 * so we never degrade the current behaviour or replay the chime obnoxiously.
 */
export function runCalibrationOnce(store, effect, opts) {
    // Sweep any pre-existing cache from prior versions of this code.
    try {
        for (let i = localStorage.length - 1; i >= 0; i--) {
            const k = localStorage.key(i);
            if (k && k.startsWith('nkf-aec.delay.'))
                localStorage.removeItem(k);
        }
    }
    catch { /* localStorage disabled — ignore */ }
    let done = false; // stop trying (applied, or gave up → passive tracker)
    let inFlight = false;
    let attempts = 0;
    const tryRun = async () => {
        if (done || inFlight)
            return;
        const state = store.getState();
        if (state['features/base/media'].audio.muted)
            return; // mic off → wait
        if (!getLocalJitsiAudioTrack(state))
            return; // mic track not attached yet
        if (!effect._workletNode)
            return; // startEffect not run yet
        inFlight = true;
        attempts++;
        try {
            const { delaySamples, delayMs, peakFloorRatio } = await effect.runInSituCalibration({
                voiceUrl: opts.voiceUrl,
                volume: opts.volume ?? 1,
                maxPlayMs: opts.maxPlayMs ?? 1200
            });
            logger.info(`[nkf-cal] attempt ${attempts}/${MAX_ATTEMPTS}: `
                + `delay=${delaySamples} (${delayMs.toFixed(1)} ms), SNR=${peakFloorRatio.toFixed(1)}`);
            if (peakFloorRatio >= PEAK_FLOOR_LOW) {
                effect.setFarDelaySamples(delaySamples, 'calibration');
                setCalibrationDebugInfo(delaySamples, delayMs, peakFloorRatio, 'insitu');
                store.dispatch(hideNotification(CAL_WARN_UID));
                done = true;
                logger.info(`[nkf-cal] applied delay=${delaySamples} (${delayMs.toFixed(1)} ms)`);
            }
            else if (attempts >= MAX_ATTEMPTS) {
                done = true;
                logger.warn('[nkf-cal] low confidence — deferring delay to passive tracker');
            }
        }
        catch (e) {
            logger.warn(`[nkf-cal] attempt ${attempts} failed:`, e);
            if (attempts >= MAX_ATTEMPTS) {
                done = true;
                logger.warn('[nkf-cal] giving up — passive tracker will converge');
            }
        }
        finally {
            inFlight = false;
        }
    };
    // Poll the mute state (the IStore surface here doesn't reliably expose
    // .subscribe()); a coarse tick is plenty for a human-scale mic-toggle.
    setInterval(() => {
        if (done)
            return;
        try {
            void tryRun();
        }
        catch (e) {
            logger.warn('[nkf-cal] poll error:', e);
        }
    }, 600);
    // Device change: re-calibrate on the next mic-on (round-trip delay changes
    // when the user swaps headset / speakers).
    if (typeof navigator !== 'undefined' && navigator.mediaDevices) {
        navigator.mediaDevices.addEventListener?.('devicechange', () => {
            logger.info('[nkf-cal] devicechange — will re-calibrate on next mic-on');
            done = false;
            attempts = 0;
        });
    }
}
